import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimPriceLevelEnum } from 'gql/graphql';
import type { BadgePropStatus } from 'uikit';
import { BadgeGroup, Loader } from 'uikit';

import { useEffect, useMemo } from 'react';

import { bimPriceBaseAnalyticsGql } from '../bimWorkOrderPriceAnalyticsQueries';
import type { TBadgeItem, TBaseRowData } from '../types';
import { statusItemProps } from '../types';

const getBadgeGroupProp =
    <T extends keyof TBadgeItem>(propName: T) =>
    (item: TBadgeItem): T extends 'text' ? string : BadgePropStatus =>
        item[propName] as T extends 'text' ? string : BadgePropStatus;

export const useBaseAnalyticsData = ({
    refWorkOrderPriceId,
    workOrderPriceId,
    priceLevel,
}: {
    workOrderPriceId: number | undefined;
    refWorkOrderPriceId: number | undefined;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const variables = useMemo(
        () => ({
            workOrderPriceId,
            refWorkOrderPriceId,
            priceLevel,
        }),
        [priceLevel, refWorkOrderPriceId, workOrderPriceId],
    );

    const withEffect = useMemo(
        () => workOrderPriceId !== undefined && refWorkOrderPriceId !== undefined,
        [refWorkOrderPriceId, workOrderPriceId],
    );

    const [data, status] = useRequest({
        requestId: 'bimPriceAnalytics',
        query: bimPriceBaseAnalyticsGql,
        variables,
        withEffect,
    });

    const [resetData] = useResetStoreData({
        requestId: 'bimPriceAnalytics',
    });

    const isLoading = status === RequestStatus.FETCHING;

    const itemsStatusBlock = useMemo(() => {
        if (isLoading) {
            return <Loader />;
        }

        const items: TBadgeItem[] | undefined = data?.bimPriceAnalytics?.data?.statusCounts?.map((item) => ({
            text: `${item.count} ${statusItemProps[item.status].label}`,
            color: statusItemProps[item.status].status,
        }));

        return (
            items && (
                <BadgeGroup
                    items={items}
                    getItemLabel={getBadgeGroupProp('text')}
                    getItemView={() => 'stroked'}
                    getItemStatus={getBadgeGroupProp('color')}
                    getItemKey={getBadgeGroupProp('text')}
                    form="round"
                />
            )
        );
    }, [data?.bimPriceAnalytics?.data?.statusCounts, isLoading]);

    const baseData = useMemo<TBaseRowData[]>(() => {
        const { refPriceValue, topPriceValue } = data?.bimPriceAnalytics?.data ?? {};

        return [
            {
                topPinnedRow: true,
                topPriceValue,
                refPriceValue,
            },
        ];
    }, [data?.bimPriceAnalytics?.data]);

    useEffect(
        () => () => {
            resetData(undefined);
        },
        [resetData],
    );

    return {
        itemsStatusBlock,
        baseData,
    };
};
